import Logging from "logging";
import DataProvider from "services/data-provider/data-provider";
import { DocProcessingStatus } from "models/doc-processing-status";
import { useAppStore } from "store/store";

import useConversionAnalytics from "./analytics/useConversionAnalytics";
import useMutatedPagePath from "./analytics/useMutatedPagePath";
import usePaxSession from "./usePaxSession";
import { PaxCheckDocumentProvisionMethod } from "api/models";
import { DocumentRequirement } from "models/checklist/checklist-item/checklist-item";
import { MultiPaxSessionId } from "models/session/session";
import useAllowedDocumentsForTravelDoc from "features/document-capture/hooks/useAllowedDocumentsForTravelDoc";
import useItemLevelNavigationState, {
  ItemNavigationState,
  ItemTypeNavigationState,
} from "./navigation/useItemLevelNavigationState";
import { useSearchParams } from "react-router-dom";
import { getGeneralAnalyticsParams } from "./analytics/useGeneralAnalytics";
import { getPaxLevelParams } from "./analytics/usePaxLevelAnalytics";
import AnalyticsService from "services/analytics/analytics";
import useDemoScenario from "./demo/useDemoScenario";
import AllowedDocument from "models/documents/allowed-document";
import PaxSession from "models/session/pax-session";
import DataProviderGeneralException, {
  DataProviderExceptionType,
} from "services/data-provider/data-provider-errors";
import SessionIneligibilityReason, {
  SessionIneligibilityReasonFromApi,
} from "models/session-ineligibility-reason";
import { ApplyDocumentErrorWrapper } from "services/data-provider/ApplyDocumentResponse";

export const base64ToFile = (
  base64: string,
  fileName: string,
  mimeType: string
) => {
  const binaryString = window.atob(base64.split(",")[1]); // Decode base64 string
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([bytes], { type: mimeType });
  const file = new File([blob], fileName, { type: mimeType });
  return file;
};

const useDocumentImagesUpload = (
  provisionMethod: PaxCheckDocumentProvisionMethod
) => {
  const locale = useAppStore((state) => state.locale);

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSessions = useAppStore((state) => state.paxSessions);
  const paxSession = usePaxSession();

  const journey = useAppStore((state) => state.journey);

  const [path, title, location] = useMutatedPagePath();

  const paxSessionId = paxSession?.paxSessionId;

  const setProcessingStatus = useAppStore((state) => state.setProcessingStatus);
  const setProcessingMayTakeLong = useAppStore(
    (state) => state.setProcessingMayTakeLong
  );

  const setProcessedDocTypes = useAppStore(
    (state) => state.setProcessedDocTypes
  );
  const setProvisionMethod = useAppStore((state) => state.setProvisionMethod);
  const sendConversionEvents = useConversionAnalytics();

  const setIneligibilityReason = useAppStore(
    (state) => state.setIneligibilityReason
  );

  const setSessionIsLocked = useAppStore((state) => state.setSessionIsLocked);

  const [searchParams] = useSearchParams();
  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  const allowedTravelDocs = useAllowedDocumentsForTravelDoc();

  const isScan = provisionMethod === PaxCheckDocumentProvisionMethod.SCAN;
  const isUpload = provisionMethod === PaxCheckDocumentProvisionMethod.UPLOAD;

  // delay function
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  let forceDocumentType: string | undefined;
  let allowedDocuments: AllowedDocument[] = [];
  let isTravelDocument: boolean = false;

  // itemId
  if (itemLevelNavigationState instanceof ItemNavigationState) {
    const docReq = itemLevelNavigationState.item as DocumentRequirement;

    // we would only enforce doc type for recognition if servers says so,
    // otherwise we get problems with passengers uploading US BCC cards as Visas
    forceDocumentType =
      itemLevelNavigationState.forceDocumentType &&
      docReq.allowedDocumentsSet.shouldUseItemResolver
        ? itemLevelNavigationState.forceDocumentType
        : undefined;
    allowedDocuments = docReq.allowedDocumentsSet.allowedDocuments;
  }

  // itemType - only TRAVEL_DOC is allowed
  if (itemLevelNavigationState instanceof ItemTypeNavigationState) {
    isTravelDocument = true;
    forceDocumentType = itemLevelNavigationState.forceDocumentType;
    allowedDocuments = allowedTravelDocs;
  }

  const documentTypes = forceDocumentType
    ? [forceDocumentType]
    : allowedDocuments.map((doc) => doc.type);

  // TODO ugly - needs reworking
  const multipaxResolutionSupported = forceDocumentType
    ? allowedDocuments.find((doc) => doc.type === forceDocumentType)
        ?.multipaxResolutionSupported || false
    : allowedDocuments.every((doc) => doc.multipaxResolutionSupported);

  const demoScenario = useDemoScenario(
    itemLevelNavigationState.forceDocumentType ??
      itemLevelNavigationState.itemType
  );

  const analyticsParams = {
    ...getGeneralAnalyticsParams(
      paxSessions,
      journey,
      path,
      title,
      location,
      locale
    ),
    ...getPaxLevelParams(paxSessions, paxSession?.paxSessionId || null),
    item_type: itemLevelNavigationState.itemType,
    is_scan: isScan,
    is_upload: isUpload,
    is_manual: false,
  };

  const upload = async (
    files: File[],
    additionalPaxSessionIds?: string[],
    pageIndexToProcess?: number
  ) => {
    if (!paxSessionId) {
      Logging.log("uploading", "missing", "No selected pax session id");
      return;
    }

    AnalyticsService.shared.trackEvent(
      "apply_started",
      analyticsParams,
      // isScanRelated - defined by provision method
      isScan
    );

    const ANALYTICS_DOCUMENT_APPLY_FAILED_PARAMS = {
      ...analyticsParams,
      failed_to_process: 0,
      id_mismatch_error: 0,
      rules_mismatch_error: 0,
    };

    setProcessedDocTypes(documentTypes);
    setProvisionMethod(provisionMethod);

    const applyDocumentFiles = async (
      sessionId: MultiPaxSessionId,
      paxSessionId: string,
      files: File[],
      method: PaxCheckDocumentProvisionMethod,
      additionalPaxSessionIds?: string[]
    ): Promise<PaxSession[]> => {
      return DataProvider.shared.applyDocumentFiles(
        sessionId,
        paxSessionId,
        multipaxResolutionSupported ? additionalPaxSessionIds || [] : [],
        files,
        method,
        isTravelDocument,
        documentTypes,
        pageIndexToProcess
      );
    };

    applyDocumentFiles(
      sessionId,
      paxSessionId,
      files,
      provisionMethod,
      additionalPaxSessionIds
    )
      .then(async (res) => {
        // success
        if (demoScenario !== undefined) {
          // demo mode - add small delay to show processing
          await delay(1000);
        }

        const previousPaxSessions = useAppStore.getState().paxSessions;

        useAppStore.getState().updatePaxSessions(res);

        setProcessingStatus(DocProcessingStatus.Success, null);
        setProcessingMayTakeLong(null);

        // clear processed doc types
        setProcessedDocTypes(undefined);

        AnalyticsService.shared.trackEvent(
          "apply_success",
          analyticsParams,
          // isScanRelated - defined by provision method
          isScan
        );

        // wait 300ms just in case - to increase chances state
        // indeed was updated
        await delay(300);

        sendConversionEvents(
          previousPaxSessions,
          useAppStore.getState().paxSessions
        );
      })
      .catch((err) => {
        if (err instanceof ApplyDocumentErrorWrapper) {
          // apply document error
          setProcessingStatus(DocProcessingStatus.Error, err.error);
          setProcessingMayTakeLong(null);

          if (err.analyticsParams.recognition_error) {
            AnalyticsService.shared.trackEvent(
              "apply_recognition_failed",
              {
                ...ANALYTICS_DOCUMENT_APPLY_FAILED_PARAMS,
                ...err.analyticsParams,
              },
              // isScanRelated - defined by provision method
              isScan
            );
          }

          AnalyticsService.shared.trackEvent(
            "apply_failed",
            {
              ...ANALYTICS_DOCUMENT_APPLY_FAILED_PARAMS,
              ...err.analyticsParams,
            },
            // isScanRelated - defined by provision method
            isScan
          );

          return;
        }

        if (
          err instanceof DataProviderGeneralException &&
          err.type !== DataProviderExceptionType.Unknown &&
          err.type !== DataProviderExceptionType.BadRequest &&
          err.type !== DataProviderExceptionType.NotFound
        ) {
          // api "general" error
          switch (err.type) {
            case DataProviderExceptionType.NotProcessable:
            case DataProviderExceptionType.NotEligible:
              // not processable or not eligible
              setIneligibilityReason(
                SessionIneligibilityReasonFromApi(err.message)
              );
              setProcessingStatus(DocProcessingStatus.Ineligible, err);
              setProcessingMayTakeLong(null);

              // ineligible
              AnalyticsService.shared.trackEvent(
                "apply_ineligible",
                ANALYTICS_DOCUMENT_APPLY_FAILED_PARAMS,
                // isScanRelated - defined by provision method
                isScan
              );
              return;

            case DataProviderExceptionType.SessionIsFinalised:
              setIneligibilityReason(
                SessionIneligibilityReason.SessionIsFinalised
              );
              return;

            case DataProviderExceptionType.SessionIsLocked:
              setSessionIsLocked(true);
              return;
          }
        }

        // unkown error
        setProcessingStatus(DocProcessingStatus.Error, "unknown_error");
        setProcessingMayTakeLong(null);

        AnalyticsService.shared.trackEvent(
          "apply_failed",
          {
            ...ANALYTICS_DOCUMENT_APPLY_FAILED_PARAMS,
            unknown_error: 1,
          },
          // isScanRelated - defined by provision method
          isScan
        );

        Logging.log(
          "uploading",
          paxSessionId,
          `Error on doc image apply ${err instanceof Error ? err.message : err}`
        );
      });
  };

  return upload;
};

export default useDocumentImagesUpload;
